import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {readableTime} from 'util/DateUtil';

import ModelRunStatusBadge from './ModelRunStatusBadge';
import type {ModelRunRow} from './ModelRunTypes';

export interface ModelHubRunsTabProps {
  onNavigateToModels: () => void;
  runs?: Array<ModelRunRow>;
  isLoading?: boolean;
}

export default function ModelHubRunsTab({
  onNavigateToModels,
  runs = [],
  isLoading = false,
}: ModelHubRunsTabProps): JSX.Element {
  if (runs.length === 0 && !isLoading) {
    return (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>No runs yet</div>
        <div className={css(styles.emptyStateSubtext)}>Go to the Models tab to run your first model.</div>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          text="Go to Models"
          onClick={onNavigateToModels}
        />
      </div>
    );
  }

  return (
    <div className={css(styles.container)}>
      <Table numRows={runs.length} isLoading={isLoading} getRowKey={(rowIndex: number) => runs[rowIndex].id}>
        <Column
          header={<Cell>Name</Cell>}
          renderCell={(rowIndex: number) => <Cell>{runs[rowIndex].name}</Cell>}
        />
        <Column
          header={<Cell>Status</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <ModelRunStatusBadge status={runs[rowIndex].status} />
            </Cell>
          )}
        />
        <Column
          header={<Cell>Model</Cell>}
          renderCell={(rowIndex: number) => <Cell>{runs[rowIndex].modelName}</Cell>}
        />
        <Column
          header={<Cell>Creator</Cell>}
          renderCell={(rowIndex: number) => <Cell>{runs[rowIndex].creatorName}</Cell>}
        />
        <Column
          header={<Cell>Created</Cell>}
          renderCell={(rowIndex: number) => <Cell>{readableTime(runs[rowIndex].createdAt)}</Cell>}
        />
        <Column
          header={<Cell>Modified</Cell>}
          renderCell={(rowIndex: number) => <Cell>{readableTime(runs[rowIndex].modifiedAt)}</Cell>}
        />
        <Column
          header={<Cell>Folder</Cell>}
          renderCell={(rowIndex: number) => <Cell>{runs[rowIndex].folderName}</Cell>}
        />
      </Table>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    textAlign: 'center',
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.NEAR_BLACK,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: Colors.GRAY_DARKER,
    marginBottom: 16,
  },
});
